import { getHlsFromVideoJsPlayer } from '@app/core/video-player/video-js/hls-playback-fix/hls-pdt-playback';
import Hls, { ErrorData } from 'hls.js';
import { useEffect, useState } from 'react';
import Player from 'video.js/dist/types/player';

/** Max errors kept in the debug panel so the list stays readable. */
const MAX_ERRORS = 20;

export type HlsTimelineErrorEntry = {
  id: string;
  at: number;
  fatal: boolean;
  type: string;
  details: string;
  message: string;
  url?: string;
};

/**
 * Builds a stable debug entry from an hls.js ERROR event payload.
 */
function entryFromHlsError(data: ErrorData): HlsTimelineErrorEntry {
  const message =
    data.error?.message ||
    data.reason ||
    data.err?.message ||
    data.response?.text ||
    'Unknown HLS error';

  return {
    id: `${Date.now()}-${data.details}-${Math.random().toString(36).slice(2, 8)}`,
    at: Date.now(),
    fatal: data.fatal,
    type: data.type,
    details: data.details,
    message,
    url: data.url || data.frag?.url || data.response?.url,
  };
}

/**
 * Builds a debug entry from the HTMLMediaElement media error.
 */
function entryFromMediaError(mediaError: MediaError): HlsTimelineErrorEntry {
  return {
    id: `${Date.now()}-media-${mediaError.code}`,
    at: Date.now(),
    fatal: true,
    type: 'mediaError',
    details: `MEDIA_ERR_${mediaError.code}`,
    message: mediaError.message || `HTMLMediaElement error code ${mediaError.code}`,
  };
}

/**
 * Subscribes to hls.js and video element errors for the HLS timeline debug panel.
 */
export function useHlsTimelineErrors(player: Player | null): HlsTimelineErrorEntry[] {
  const [errors, setErrors] = useState<HlsTimelineErrorEntry[]>([]);

  useEffect(() => {
    if (!player || player.isDisposed()) {
      setErrors([]);
      return;
    }

    const hls = getHlsFromVideoJsPlayer(player);
    if (!hls) {
      setErrors([]);
      return;
    }

    const pushError = (entry: HlsTimelineErrorEntry) => {
      setErrors((prev) => [entry, ...prev].slice(0, MAX_ERRORS));
    };

    const onHlsError = (_event: string, data: ErrorData) => {
      pushError(entryFromHlsError(data));
    };

    const videoEl = player.tech(false)?.el() as HTMLMediaElement | undefined;
    const onVideoError = () => {
      if (videoEl?.error) {
        pushError(entryFromMediaError(videoEl.error));
      }
    };

    hls.on(Hls.Events.ERROR, onHlsError);
    videoEl?.addEventListener('error', onVideoError);

    return () => {
      hls.off(Hls.Events.ERROR, onHlsError);
      videoEl?.removeEventListener('error', onVideoError);
      setErrors([]);
    };
  }, [player]);

  return errors;
}
